import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Users, Shield, ArrowRight } from 'lucide-react';

const HEADLINES = [
  { main: 'El mes se acaba.', highlight: 'Tu plata también.' },
  { main: '¿A dónde se fue', highlight: 'tu plata?' },
  { main: 'Vives contando los días', highlight: 'para la quincena.' },
  { main: 'El dinero no te alcanza', highlight: 'porque no lo ves.' },
];

const SOCIAL_PROOF = {
  rating: '4.9',
  users: '+12,000',
  reviews: '+2,500',
};

function MockupDashboard() {
  const [view, setView] = useState('balance');

  return (
    <div className="space-y-3 select-none">
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
        {['balance', 'categories'].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex-1 text-[9px] font-semibold py-1 rounded-md transition-all ${
              view === v
                ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-400'
            }`}
          >
            {v === 'balance' ? 'Balance' : 'Categorias'}
          </button>
        ))}
      </div>

      {view === 'balance' ? (
        <>
          <div className="text-lg font-extrabold text-gray-900 dark:text-white">
            $2,924,500
          </div>
          <div className="flex gap-3 text-[10px]">
            <span className="text-emerald-500 font-semibold">+$4,300,000</span>
            <span className="text-red-400 font-semibold">-$1,375,500</span>
          </div>
          <div className="flex items-end gap-[3px] h-8 pt-1">
            {[40, 25, 55, 35, 60, 45, 70].map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm bg-brand-400/60 dark:bg-brand-500/40 transition-all hover:bg-brand-500"
                style={{ height: `${h}%` }}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="space-y-2">
          {[
            { name: 'Comida', pct: 65, color: 'bg-amber-400', amount: '$390k' },
            { name: 'Transporte', pct: 40, color: 'bg-blue-400', amount: '$240k' },
            { name: 'Vivienda', pct: 87, color: 'bg-purple-400', amount: '$520k' },
          ].map((item) => (
            <div key={item.name}>
              <div className="flex justify-between text-[9px] mb-1">
                <span className="text-gray-500 dark:text-gray-400">{item.name}</span>
                <span className="font-bold text-gray-500 dark:text-gray-400">{item.amount}</span>
              </div>
              <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${item.color} rounded-full transition-all duration-700`}
                  style={{ width: `${item.pct}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MockupTransaction() {
  const allTx = [
    { icon: '🍽️', name: 'Supermercado', date: 'Hoy', amount: '-$85,500', color: 'text-red-400', bg: 'bg-amber-100 dark:bg-amber-500/20' },
    { icon: '💰', name: 'Salario', date: 'Feb 1', amount: '+$3,500,000', color: 'text-emerald-500', bg: 'bg-emerald-100 dark:bg-emerald-500/20' },
    { icon: '🏠', name: 'Arriendo', date: 'Feb 1', amount: '-$450,000', color: 'text-red-400', bg: 'bg-purple-100 dark:bg-purple-500/20' },
  ];

  const [dismissed, setDismissed] = useState([]);
  const visible = allTx.filter((_, i) => !dismissed.includes(i));

  return (
    <div className="space-y-2 select-none">
      <div className="flex justify-between items-center mb-1">
        <span className="text-[9px] text-gray-400 uppercase tracking-wider font-semibold">Movimientos</span>
        {dismissed.length > 0 && (
          <button
            onClick={() => setDismissed([])}
            className="text-[9px] text-brand-500 hover:text-brand-600 font-semibold"
          >
            Restaurar
          </button>
        )}
      </div>
      {visible.map((tx) => {
        const realIndex = allTx.indexOf(tx);
        return (
          <div
            key={tx.name + tx.date}
            onClick={() => setDismissed((prev) => [...prev, realIndex])}
            className="flex items-center gap-2.5 cursor-pointer rounded-lg p-1 -m-1 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all group"
            title="Click para descartar"
          >
            <div className={`w-8 h-8 rounded-lg ${tx.bg} flex items-center justify-center text-sm`}>
              {tx.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-gray-900 dark:text-white truncate">{tx.name}</div>
              <div className="text-[10px] text-gray-400">{tx.date}</div>
            </div>
            <div className={`text-xs font-bold ${tx.color}`}>{tx.amount}</div>
          </div>
        );
      })}
    </div>
  );
}

function MockupGoal() {
  const [saved, setSaved] = useState(800);
  const target = 2000;
  const pct = Math.min(Math.round((saved / target) * 100), 100);
  const isComplete = saved >= target;

  return (
    <div className="space-y-3 select-none">
      <div className="flex items-center gap-2">
        <span className="text-lg">✈️</span>
        <div className="flex-1">
          <div className="text-xs font-bold text-gray-900 dark:text-white">Vacaciones</div>
          <div className="text-[10px] text-gray-400">${saved.toLocaleString()} / ${target.toLocaleString()}</div>
        </div>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-400' : 'bg-gradient-to-r from-brand-500 to-accent-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <button
        onClick={() => setSaved((s) => Math.min(s + 200, target))}
        disabled={isComplete}
        className={`w-full text-[10px] font-semibold py-1.5 rounded-lg transition-all ${
          isComplete
            ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
            : 'bg-brand-50 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 hover:bg-brand-100 dark:hover:bg-brand-500/20'
        }`}
      >
        {isComplete ? 'Completada!' : `+ Ahorrar $200`}
      </button>
    </div>
  );
}

export default function HeroSection2({ onCta }) {
  const { t } = useTranslation();
  const [currentHeadline, setCurrentHeadline] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  // Rotate headlines every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentHeadline((prev) => (prev + 1) % HEADLINES.length);
        setIsAnimating(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const headline = HEADLINES[currentHeadline];

  return (
    <section className="pt-28 sm:pt-32 pb-16 sm:pb-20 px-4 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-50/50 via-white to-white dark:from-brand-950/20 dark:via-gray-950 dark:to-gray-950 -z-10" />
      
      {/* Floating decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-brand-200/20 dark:bg-brand-500/10 rounded-full blur-3xl -z-10" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-200/20 dark:bg-accent-500/10 rounded-full blur-3xl -z-10" />

      <div className="max-w-5xl mx-auto">
        {/* Social Proof Bar */}
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-8">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex -space-x-1">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="w-5 h-5 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 border-2 border-white dark:border-gray-800"
                />
              ))}
            </div>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
              {SOCIAL_PROOF.users} usuarios
            </span>
          </div>
          
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 shadow-sm">
            <Star size={14} className="text-amber-400 fill-amber-400" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
              {SOCIAL_PROOF.rating} ({SOCIAL_PROOF.reviews} reviews)
            </span>
          </div>

          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 shadow-sm">
            <Shield size={14} className="text-emerald-500" />
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
              Datos encriptados
            </span>
          </div>
        </div>

        {/* Main Headline */}
        <div className="text-center mb-6">
          <h1 
            className={`text-4xl sm:text-5xl lg:text-6xl font-display font-extrabold text-gray-900 dark:text-white leading-tight transition-all duration-300 ${
              isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
            }`}
          >
            {headline.main}
            <br />
            <span className="text-gradient">{headline.highlight}</span>
          </h1>
        </div>

        {/* Subtitle */}
        <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-center mb-8">
          Kuadra2 te ayuda a ordenar tus finanzas personales y en pareja. 
          Sin complicaciones. Sin juicios.{' '}
          <span className="font-semibold text-brand-600 dark:text-brand-400">
            Gratis para empezar.
          </span>
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <button 
            onClick={onCta} 
            className="btn-primary text-lg py-4 px-8 flex items-center justify-center gap-2 group"
          >
            {t('landing.hero.cta')}
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
          <a 
            href="#how-it-works" 
            className="btn-secondary text-lg py-4 px-8 flex items-center justify-center"
          >
            {t('landing.hero.ctaSecondary')}
          </a>
        </div>

        {/* No credit card note */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-12">
          ✓ No requiere tarjeta de crédito • ✓ Setup en 30 segundos • ✓ Cancela cuando quieras
        </p>

        {/* Headline indicators */}
        <div className="flex justify-center gap-2 mb-12">
          {HEADLINES.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentHeadline(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === currentHeadline 
                  ? 'w-6 bg-brand-500' 
                  : 'w-1.5 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>

        {/* Mockup cards */}
        <div className="flex justify-center gap-4 sm:gap-6 max-w-3xl mx-auto">
          <div className="animate-float hidden sm:block w-48 sm:w-56 rounded-2xl shadow-lg bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 -rotate-2">
            <MockupDashboard />
          </div>
          <div className="animate-float-delayed w-56 sm:w-60 rounded-2xl shadow-xl bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 z-10 scale-105">
            <MockupTransaction />
          </div>
          <div className="animate-float-slow hidden sm:block w-48 sm:w-56 rounded-2xl shadow-lg bg-white dark:bg-gray-800 p-4 border border-gray-100 dark:border-gray-700 rotate-2">
            <MockupGoal />
          </div>
        </div>
      </div>
    </section>
  );
}
